import { useRef, useState, useCallback, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { Camera, ImagePlus, Type, X, Loader2, Sparkles, Info, Plus, ChevronLeft, ChevronRight, CalendarDays, Edit3 } from 'lucide-react';
import type { ImageInput } from '../../types';
import { supabase } from '../../lib/supabase';
import { hasClaudeApiKey } from '../../services/claudeApi';

/** Airports/airlines with flight schedule data for optimized image parsing */
const SUPPORTED_HUBS = new Set(['DXB']);
const SUPPORTED_AIRLINES = new Set(['EK']); // Emirates

const DISMISS_KEY = 'rostfound_airport_banner_dismissed';
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type InputMode = 'cards' | 'photo' | 'text';

interface RosterPasteInputProps {
  rosterText: string;
  setRosterText: (v: string) => void;
  images: ImageInput[];
  onAddImage: (file: File) => void;
  onRemoveImage: (index: number) => void;
  onAddCameraImage?: () => void;
  onSubmit: () => void;
  onCancel: () => void;
  parsing: boolean;
  error: string | null;
  userAirline?: string;
  userBase?: string;
  userId?: string;
  rosterMonth: number;
  rosterYear: number;
  onMonthChange: (month: number, year: number) => void;
  onManualEntry?: () => void;
  onCalendarImport?: () => void;
  /** Skip cards and go straight to a specific mode (e.g. from share extension) */
  initialMode?: InputMode;
}

export function RosterPasteInput({
  rosterText,
  setRosterText,
  images,
  onAddImage,
  onRemoveImage,
  onAddCameraImage,
  onSubmit,
  // onCancel not used — Back button returns to cards view
  parsing,
  error,
  userAirline,
  userBase,
  userId,
  rosterMonth,
  rosterYear,
  onMonthChange,
  onManualEntry,
  onCalendarImport,
  initialMode,
}: RosterPasteInputProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [inputMode, setInputMode] = useState<InputMode>(initialMode ?? 'cards');

  // If images/text arrive externally (share extension), switch to the right mode
  useEffect(() => {
    if (images.length > 0 && inputMode === 'cards') setInputMode('photo');
    if (rosterText.trim() && inputMode === 'cards') setInputMode('text');
  }, [images.length, rosterText]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-focus textarea when switching to text mode
  useEffect(() => {
    if (inputMode === 'text') {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [inputMode]);

  const isFullySupported =
    (userBase && SUPPORTED_HUBS.has(userBase)) ||
    (userAirline && SUPPORTED_AIRLINES.has(userAirline));

  const [bannerDismissed, setBannerDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === '1'
  );

  const showBanner = !isFullySupported && !bannerDismissed;

  const dismissBanner = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, '1');
    setBannerDismissed(true);
    if (userId && userAirline && userBase) {
      supabase
        .from('airport_support_waitlist' as never)
        .upsert(
          { user_id: userId, airline_code: userAirline, base_airport: userBase } as never,
          { onConflict: 'user_id,airline_code,base_airport' }
        )
        .then(() => {});
    }
  }, [userId, userAirline, userBase]);

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData.items;
    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          onAddImage(file);
          setInputMode('photo');
        }
        return;
      }
    }
  };

  const handleCameraCard = () => {
    if (onAddCameraImage) {
      onAddCameraImage();
      setInputMode('photo');
    }
  };

  const handleUploadCard = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onAddImage(file);
      setInputMode('photo');
    }
    e.target.value = '';
  };

  const handleBackToCards = () => {
    setInputMode('cards');
  };

  const hasLLM = hasClaudeApiKey();
  const isNative = Capacitor.isNativePlatform();
  const hasContent = rosterText.trim().length > 0 || images.length > 0;
  const lineCount = rosterText.trim() ? rosterText.trim().split('\n').length : 0;

  return (
    <div className="flex flex-col gap-3">
      {showBanner && (
        <div className="bg-amber-900/30 border border-amber-700/40 rounded-lg px-3 py-2.5 flex gap-2.5 items-start">
          <Info size={16} className="text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-amber-200 leading-relaxed">
              Image parsing works best for Emirates (DXB) right now.
              Text paste is more reliable for other airlines.
              We'll notify you when {userBase || 'your airport'} is fully supported!
            </p>
            <button
              onClick={dismissBanner}
              className="text-[10px] text-amber-400/70 mt-1 underline underline-offset-2"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Month selector */}
      <div className="flex items-center justify-between bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2">
        <span className="text-xs text-slate-400">Roster for</span>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => {
              const m = rosterMonth === 1 ? 12 : rosterMonth - 1;
              const y = rosterMonth === 1 ? rosterYear - 1 : rosterYear;
              onMonthChange(m, y);
            }}
            disabled={parsing}
            className="p-0.5 rounded hover:bg-slate-600 text-slate-400 disabled:opacity-50"
          >
            <ChevronLeft size={14} />
          </button>
          <span className="text-sm font-medium text-slate-100 min-w-[80px] text-center">
            {MONTH_NAMES[rosterMonth - 1]} {rosterYear}
          </span>
          <button
            type="button"
            onClick={() => {
              const m = rosterMonth === 12 ? 1 : rosterMonth + 1;
              const y = rosterMonth === 12 ? rosterYear + 1 : rosterYear;
              onMonthChange(m, y);
            }}
            disabled={parsing}
            className="p-0.5 rounded hover:bg-slate-600 text-slate-400 disabled:opacity-50"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* ── Input cards (initial state) ── */}
      {inputMode === 'cards' && hasLLM && (() => {
        const showCamera = isNative && !!onAddCameraImage;
        const cols = showCamera ? 'grid-cols-3' : 'grid-cols-2';
        return (
          <div className={`grid ${cols} gap-2.5`}>
            {/* Camera card — native only */}
            {showCamera && (
              <button
                data-testid="entry-camera"
                onClick={handleCameraCard}
                disabled={parsing}
                className="flex flex-col items-center py-5 px-2 border border-slate-600 rounded-xl bg-slate-700/30 hover:bg-slate-700/60 hover:border-slate-500 transition-colors text-center disabled:opacity-50"
              >
                <div className="w-11 h-11 rounded-[14px] bg-accent/12 flex items-center justify-center mb-2.5">
                  <Camera size={22} className="text-accent" />
                </div>
                <span className="text-xs font-semibold text-slate-100">Camera</span>
                <span className="text-[10px] text-slate-500 mt-0.5">Take a photo</span>
              </button>
            )}

            {/* Upload card */}
            <button
              data-testid="entry-upload"
              onClick={handleUploadCard}
              disabled={parsing}
              className="flex flex-col items-center py-5 px-2 border border-slate-600 rounded-xl bg-slate-700/30 hover:bg-slate-700/60 hover:border-slate-500 transition-colors text-center disabled:opacity-50"
            >
              <div className="w-11 h-11 rounded-[14px] bg-teal/12 flex items-center justify-center mb-2.5">
                <ImagePlus size={22} className="text-teal" />
              </div>
              <span className="text-xs font-semibold text-slate-100">Upload</span>
              <span className="text-[10px] text-slate-500 mt-0.5">Choose a photo</span>
            </button>

            {/* Paste text card */}
            <button
              data-testid="entry-roster"
              onClick={() => setInputMode('text')}
              disabled={parsing}
              className="flex flex-col items-center py-5 px-2 border border-slate-600 rounded-xl bg-slate-700/30 hover:bg-slate-700/60 hover:border-slate-500 transition-colors text-center disabled:opacity-50"
            >
              <div className="w-11 h-11 rounded-[14px] bg-sage/12 flex items-center justify-center mb-2.5">
                <Type size={22} className="text-sage" />
              </div>
              <span className="text-xs font-semibold text-slate-100">Paste</span>
              <span className="text-[10px] text-slate-500 mt-0.5">Text schedule</span>
            </button>
          </div>
        );
      })()}

      {/* Fallback: no LLM key — show just text paste */}
      {inputMode === 'cards' && !hasLLM && (
        <div className="text-center py-4">
          <p className="text-xs text-slate-500">AI import unavailable</p>
        </div>
      )}

      {/* ── Photo mode: file card with thumbnails ── */}
      {inputMode === 'photo' && (
        <div className="flex gap-3 items-center p-3 border border-accent/15 rounded-xl bg-accent/[0.03]">
          <div className="flex gap-1.5 flex-wrap">
            {images.map((img, i) => (
              <div key={i} className="relative w-13 h-13 rounded-lg overflow-hidden border border-slate-600 shrink-0">
                <img
                  src={`data:${img.mediaType};base64,${img.base64Data}`}
                  alt={`Upload ${i + 1}`}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => {
                    onRemoveImage(i);
                    if (images.length <= 1) setInputMode('cards');
                  }}
                  className="absolute -top-0.5 -right-0.5 w-[18px] h-[18px] bg-slate-900 border border-slate-600 rounded-full flex items-center justify-center"
                >
                  <X size={8} className="text-slate-400" />
                </button>
              </div>
            ))}
            {/* Placeholder when camera was opened but no image yet */}
            {images.length === 0 && (
              <div className="w-13 h-13 rounded-lg border border-slate-600 bg-slate-700/50 flex items-center justify-center">
                <ImagePlus size={18} className="text-slate-500" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-100">
              {images.length === 0 ? 'No photo yet' : `${images.length} photo${images.length !== 1 ? 's' : ''}`}
            </p>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {images.length === 0 ? 'Take or upload a photo' : 'Ready to import'}
            </p>
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="px-2.5 py-1.5 text-[11px] font-medium text-accent border border-accent/20 rounded-lg hover:bg-accent/5 shrink-0"
          >
            <Plus size={12} className="inline -mt-px mr-0.5" />
            Add
          </button>
        </div>
      )}

      {/* ── Text mode: bordered text zone ── */}
      {inputMode === 'text' && (
        <div className="border border-slate-600 rounded-xl p-3.5 bg-slate-700/20 focus-within:border-accent/30 transition-colors">
          {lineCount > 0 && (
            <div className="flex items-center gap-1.5 mb-2 text-[11px] text-sage font-medium">
              <Sparkles size={12} />
              {lineCount} line{lineCount !== 1 ? 's' : ''} pasted
            </div>
          )}
          <textarea
            ref={textareaRef}
            value={rosterText}
            onChange={(e) => setRosterText(e.target.value)}
            onPaste={handlePaste}
            placeholder={`Paste your flight schedule here...\n\n05MAR EK203 DXB-FRA 10:00\n06MAR OFF\n07MAR EK204 FRA-DXB 14:30`}
            rows={5}
            disabled={parsing}
            className="w-full text-sm font-mono resize-none disabled:opacity-50 bg-transparent text-slate-100 placeholder-slate-500 outline-none"
          />
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleFileChange}
      />

      {error && <p className="text-xs text-red-400">{error}</p>}

      {/* Import / Back buttons — only when in photo or text mode */}
      {inputMode !== 'cards' && (
        <div className="flex gap-2 mt-1">
          <button
            onClick={onSubmit}
            disabled={parsing || !hasContent}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-accent text-white text-sm font-medium rounded-lg hover:bg-accent-dark disabled:opacity-50"
          >
            {parsing ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Sparkles size={16} />
                Import
              </>
            )}
          </button>
          <button
            onClick={handleBackToCards}
            disabled={parsing}
            className="px-4 py-2.5 text-sm font-medium rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-700 disabled:opacity-50"
          >
            Back
          </button>
        </div>
      )}

      {/* Alt methods — only show on cards view */}
      {inputMode === 'cards' && (
        <div className="flex gap-2 pt-2 border-t border-slate-700/50 mt-1">
          {isNative && onCalendarImport && (
            <button
              onClick={onCalendarImport}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-slate-500 border border-slate-700 rounded-full hover:text-slate-300 hover:border-slate-600 transition-colors"
            >
              <CalendarDays size={12} />
              Calendar
            </button>
          )}
          {onManualEntry && (
            <button
              onClick={onManualEntry}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-slate-500 border border-slate-700 rounded-full hover:text-slate-300 hover:border-slate-600 transition-colors"
            >
              <Edit3 size={12} />
              Manual entry
            </button>
          )}
        </div>
      )}
    </div>
  );
}
